/**
 * «¿Lo leímos bien?» (DISENO §3, PRODUCTO §7): lo que la pantalla de revisión hace con lo que manda el backend. El
 * desplegable de cada dato, el mapeo nuevo cuando la persona elige otra columna, cómo se ve cada fila y los números
 * del resumen. Puro y testeado (`revision.test.ts`).
 */

import { num, plural } from "../../campana-nueva/utils/format";
import type { Archivo, Carga, CampoId, CampoRevisado, Mapeo, Origen, Resumen } from "../types";

/** El valor del desplegable sin columna. */
export const SIN_COLUMNA = "";
/** El valor del desplegable para un origen que no es una columna sola («Total − Envío», «Celular (o Telefono)»). */
export const ACTUAL = "__actual__";

const columnaSola = (o: Origen | null): string | null => (o && o.tipo === "columna" && !o.respaldo?.length ? o.columna : null);

/** Lo que está elegido en el desplegable de un dato. */
export function valorDe(c: CampoRevisado): string {
  if (!c.origen) return SIN_COLUMNA;
  return columnaSola(c.origen) ?? ACTUAL;
}

/**
 * Las opciones del desplegable: las columnas del archivo (las candidatas primero, como las ordena el backend) y, si
 * el origen es una cuenta o columnas unidas, ése primero con su nombre. Un dato opcional se puede dejar sin columna;
 * uno obligatorio sin columna muestra «Elegí una columna» hasta que se elige.
 */
export function opcionesDe(c: CampoRevisado): { valor: string; texto: string }[] {
  const o: { valor: string; texto: string }[] = [];
  if (!c.obligatorio) o.push({ valor: SIN_COLUMNA, texto: "Sin columna" });
  else if (!c.origen) o.push({ valor: SIN_COLUMNA, texto: "Elegí una columna" });
  if (valorDe(c) === ACTUAL) o.push({ valor: ACTUAL, texto: c.columna });
  for (const col of c.opciones) o.push({ valor: col, texto: col });
  return o;
}

/** El mapeo con lo que se eligió para un dato (los demás quedan como estaban), o null si no cambia nada. */
export function conColumna(mapeo: Mapeo, c: CampoRevisado, valor: string): Mapeo | null {
  if (valor === ACTUAL || valor === valorDe(c)) return null;
  const origen: Origen | null = valor === SIN_COLUMNA ? null : { tipo: "columna", columna: valor };
  return { ...mapeo, [c.campo as CampoId]: origen };
}

export type EstadoCampo = "ok" | "revisar" | "falta" | "sin_columna";

/** Cómo se ve la fila: bien, para mirar (la columna es dudosa o hay valores que no se leen), falta o sin columna. */
export function estadoCampo(c: CampoRevisado): EstadoCampo {
  if (!c.origen) return c.obligatorio ? "falta" : "sin_columna";
  if (c.confianza === "revisar" || c.noLeidos > 0) return "revisar";
  return "ok";
}

export const TEXTO_ESTADO: Record<EstadoCampo, string> = {
  ok: "Correcto",
  revisar: "Revisalo",
  falta: "Falta elegir la columna",
  sin_columna: "Sin columna",
};

/** Los datos de cada archivo, en el orden en que los manda el backend. */
export function camposDe(campos: CampoRevisado[], archivo: Archivo): CampoRevisado[] {
  return campos.filter((c) => c.archivo === archivo);
}

/** «12 sin leer» (de las filas con dato). */
export const textoNoLeidos = (c: Pick<CampoRevisado, "noLeidos">) => (c.noLeidos ? `${num(c.noLeidos)} sin leer` : "");

/** «94,3%», con un decimal como los avisos del backend. Sin total, un guion. */
export function porciento(parte: number, total: number): string {
  if (!total) return "—";
  return `${new Intl.NumberFormat("es-AR", { maximumFractionDigits: 1 }).format((parte / total) * 100)}%`;
}

/** «2023-06-07T20:42» → «07/06/2023»: la hora de pared del comercio, sin pasar por un huso. */
export function fechaLocal(isoLocal: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoLocal || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "—";
}

/** Los cuatro números de «Lo que vamos a guardar», con su línea de abajo. */
export function numerosResumen(r: Resumen): { etiqueta: string; valor: string; detalle: string }[] {
  return [
    { etiqueta: "Pedidos", valor: num(r.pedidos), detalle: r.compradores ? `de ${num(r.compradores)} ${plural(r.compradores, "comprador", "compradores")}` : "" },
    { etiqueta: "Personas", valor: num(r.personas), detalle: r.nuncaCompraron ? `${num(r.nuncaCompraron)} nunca ${plural(r.nuncaCompraron, "compró", "compraron")}` : "" },
    { etiqueta: "Desde", valor: fechaLocal(r.desde), detalle: r.hasta ? `hasta el ${fechaLocal(r.hasta)}` : "" },
    { etiqueta: "Con celular", valor: porciento(r.conCelular, r.personas), detalle: `Con mail: ${porciento(r.conMail, r.personas)}` },
  ];
}

export type PasoSubida = "subir" | "leyendo" | "revisar" | "guardando" | "listo";

/** En qué paso está una carga (sin carga, subir). */
export function pasoDe(c: Pick<Carga, "estado"> | null): PasoSubida {
  switch (c?.estado) {
    case "leyendo":
      return "leyendo";
    case "para_revisar":
      return "revisar";
    case "guardando":
      return "guardando";
    case "guardada":
      return "listo";
    default:
      return "subir";
  }
}

/** Los dos archivos llegaron enteros al servidor. */
export const archivosCompletos = (c: Pick<Carga, "archivos"> | null) => !!c?.archivos.ventas?.completo && !!c?.archivos.clientes?.completo;
